import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge } from "../lib/api";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Scale, ArrowLeft, CheckCircle2, XCircle, Paperclip, Inbox } from "lucide-react";

export default function ArbitrationPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [report, setReport] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadQueue = useCallback(() => {
    api.get("/expense-reports", { params: { status: "BISHOP_ARBITRATION", for_arbitration: true, page_size: 50 } })
      .then(({ data }) => setQueue(data.reports))
      .catch(() => setQueue([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    if (reportId) {
      api.get(`/expense-reports/${reportId}`)
        .then(({ data }) => {
          setReport(data.report);
          const contested = data.report.contestations?.[data.report.contestations.length - 1];
          const init = {};
          (contested?.expense_ids || []).forEach((id) => { init[id] = "approved"; });
          setDecisions(init);
        })
        .catch((e) => { toast.error(formatApiError(e)); navigate("/arbitrage"); });
    } else {
      setReport(null);
    }
  }, [reportId, navigate]);

  const openAttachment = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/preview`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitArbitration = async () => {
    if (reason.trim().length < 5) return toast.error("Veuillez motiver votre décision (au moins 5 caractères).");
    if (!window.confirm("Confirmer l'arbitrage ? Cette décision est finale.")) return;
    setSending(true);
    try {
      const items = Object.entries(decisions).map(([expense_id, decision]) => ({ expense_id, decision }));
      await api.post(`/contests/${report.id}/arbitration`, { items, reason: reason.trim() });
      toast.success("Arbitrage enregistré. Le demandeur et la comptabilité ont été notifiés.");
      setReport(null);
      navigate("/arbitrage");
      loadQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (report) {
    const contestation = report.contestations?.[report.contestations.length - 1];
    const contestedIds = contestation?.expense_ids || [];
    const contestedExpenses = report.expenses.filter((e) => contestedIds.includes(e.id));
    return (
      <div className="space-y-6 animate-enter" data-testid="arbitration-detail">
        <Link to="/arbitrage" className="text-[#0369a1] font-semibold inline-flex items-center gap-2" data-testid="arbitration-back-link">
          <ArrowLeft className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Retour à la liste
        </Link>
        <div className="card-base">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>
            Arbitrage — {report.number}
          </h1>
          <p className="text-lg text-slate-600 mt-1">{report.requester_name} — période {report.period}</p>
        </div>

        {contestation && (
          <div className="card-base !border-orange-300 border-2" data-testid="contestation-summary">
            <h2 className="text-xl font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Scale className="h-6 w-6 text-[#c2410c]" aria-hidden="true" strokeWidth={2.5} /> Argument du demandeur
            </h2>
            <p className="text-lg text-slate-700 italic">« {contestation.reason} »</p>
          </div>
        )}

        <div className="space-y-4">
          {contestedExpenses.map((e) => {
            const refusalDecision = (report.decisions || []).filter((d) => d.expense_id === e.id && d.phase === "validation").pop();
            return (
              <div key={e.id} className="card-base !p-5 border-2" data-testid={`arbitration-expense-${e.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-60">
                    <p className="font-bold text-lg text-slate-900">{e.category_label} — {fmtEuros(e.amount_cents)}</p>
                    <p className="text-slate-600">{e.date} — {e.purpose}</p>
                    {refusalDecision?.reason && (
                      <p className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 text-red-800 font-medium">
                        Motif du refus initial : {refusalDecision.reason}
                      </p>
                    )}
                    {(e.attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {e.attachments.map((a) => (
                          <button key={a.id} onClick={() => openAttachment(a)}
                            className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 border-slate-300 text-[#0369a1] font-semibold hover:bg-sky-50"
                            data-testid={`arbitration-attachment-${a.id}`}>
                            <Paperclip className="h-4 w-4" aria-hidden="true" /> {a.filename}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 mt-4 flex-col sm:flex-row">
                  <button onClick={() => setDecisions((p) => ({ ...p, [e.id]: "approved" }))}
                    className={`flex-1 h-14 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                      decisions[e.id] === "approved" ? "bg-[#15803d] text-white" : "bg-white text-[#15803d] border-2 border-[#15803d]"
                    }`} data-testid={`arbitrate-approve-${e.id}`}>
                    <CheckCircle2 className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Donner raison au demandeur
                  </button>
                  <button onClick={() => setDecisions((p) => ({ ...p, [e.id]: "rejected" }))}
                    className={`flex-1 h-14 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                      decisions[e.id] === "rejected" ? "bg-[#b91c1c] text-white" : "bg-white text-[#b91c1c] border-2 border-[#b91c1c]"
                    }`} data-testid={`arbitrate-reject-${e.id}`}>
                    <XCircle className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Maintenir le refus
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="card-base space-y-4 border-2 !border-slate-300">
          <div>
            <Label htmlFor="arbitration-reason" className="label-lg">Motivation de la décision (obligatoire)</Label>
            <Textarea id="arbitration-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              className="text-lg border-2 border-slate-300 rounded-xl px-4 py-3 min-h-28 w-full"
              placeholder="Ex : après examen des justificatifs complémentaires, la dépense est légitime…"
              data-testid="arbitration-reason-input" />
          </div>
          <button onClick={submitArbitration} disabled={sending} className="btn-primary-lg w-full" data-testid="submit-arbitration-button">
            {sending ? "Enregistrement…" : "Rendre ma décision (finale)"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-enter" data-testid="arbitration-queue">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Contestations à arbitrer</h1>
        <p className="text-lg text-slate-600 mt-1">Désaccords transmis au niveau de l'Évêque. Votre décision est finale.</p>
      </div>
      {loading ? (
        <p className="text-lg text-slate-600">Chargement…</p>
      ) : queue.length === 0 ? (
        <div className="card-base text-center py-12" data-testid="empty-arbitration-queue">
          <Inbox className="h-16 w-16 text-slate-300 mx-auto mb-4" aria-hidden="true" strokeWidth={2.5} />
          <p className="text-xl font-semibold text-slate-700">Aucune contestation en attente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {queue.map((r) => (
            <button key={r.id} onClick={() => navigate(`/arbitrage/${r.id}`)}
              className="w-full text-left card-base !p-5 hover:border-[#c2410c] border-2 transition-colors"
              data-testid={`arbitration-report-${r.id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-bold text-lg text-slate-900">{r.number} — {r.requester_name}</p>
                  <p className="text-slate-600">Période {r.period}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-xl text-[#c2410c]">Refusé : {fmtEuros(r.total_rejected_cents)}</p>
                  <StatusBadge status={r.status} label={r.status_label} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
